import React from "react";

import { Box, Divider, Grid, MenuItem, SelectChangeEvent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Input, Select } from "../../../../../../../../components/Form";
import MapInput from "../../../../../../../../components/Form/MapInput";
import PhoneInput from "../../../../../../../../components/Form/PhoneInput";
import SkeletonComponent from "../../../../../../../../components/Generic/Skeleton";
import useYupValidation from "../../../../../../../../hooks/useYupValidation";
import { Location } from "../../../../../../../../models/location";
import {
  Subcontractor,
  SubcontractorAddresses,
} from "../../../../../../../../models/subcontractor";
import { useSubcontractorActions } from "../../hooks/useActions";
import { useSubcontractorContext } from "../context/Subcontractor";
import useStyles, { BillingAddressContainer, FieldContainer } from "./styles";

type DetailsProps = {
  errors: Array<yup.ValidationError>;
  isView: boolean;
};

const Details: React.FC<DetailsProps> = ({ errors, isView }) => {
  const {
    subcontractor: fields,
    onUpdateSubcontractor,
    setSubcontractor,
  } = useSubcontractorContext();

  const { getLanguagesAvailable } = useSubcontractorActions();
  const { data: languageList, isFetching: isLoadingLanguages } = getLanguagesAvailable();

  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const isDisabled = !!isView;

  const onChangeField = (key: keyof Subcontractor, event: React.ChangeEvent<any>) => {
    if (event && event.target) {
      onUpdateSubcontractor(key, event.target.value);
    }
  };

  const onSetField = (key: keyof Subcontractor, value: any) => {
    onUpdateSubcontractor(key, value);
  };

  const onSetAddressField = (
    objectKey: keyof SubcontractorAddresses,
    propertyKey: keyof Location,
    value: any
  ) => {
    setSubcontractor((prev) => {
      const get = prev[objectKey] || {};

      get[propertyKey] = value;

      return { ...prev, [objectKey]: get };
    });
  };

  return (
    <>
      <Box>
        <Typography className={classes.formHeadingFirstTitle}>
          {translate("businessDetails")}
        </Typography>
        <FieldContainer container spacing={3}>
          <Grid item xs={4}>
            <Input
              required
              name="businessName"
              label={translate("businessName")}
              value={fields.businessName}
              onChange={(event) => onChangeField("businessName", event)}
              placeholder={translate("typeHere")}
              error={fieldHasError("businessName")}
              errorMessage={getErrorByField("businessName")}
              disabled={isDisabled}
            />
          </Grid>
          <Grid item xs={4}>
            <PhoneInput
              name="BusinessPhone"
              defaultCountry="AU"
              international
              placeholder="61430111900"
              label={translate("businessPhone")}
              error={!!getErrorByField("businessPhone")}
              errorMessage={getErrorByField("businessPhone")}
              value={fields.businessPhone}
              onChange={(value) => onSetField("businessPhone", value)}
              disabled={isDisabled}
            />
          </Grid>

          <Grid item xs={4}>
            <Input
              required
              name="businessEmail"
              label={translate("mainEmailAddress")}
              value={fields.businessEmail}
              onChange={(event) => onChangeField("businessEmail", event)}
              placeholder={translate("typeHere")}
              error={fieldHasError("businessEmail")}
              errorMessage={getErrorByField("businessEmail")}
              disabled={isDisabled}
            />
          </Grid>
        </FieldContainer>
        <Typography className={classes.formHeading}>{translate("businessAddress")}</Typography>
        <FieldContainer container spacing={3}>
          <Grid item xs={8}>
            <MapInput
              required
              location={fields?.businessLocation}
              onChange={(location) => {
                onSetField("businessLocation", location);
              }}
              label={translate("registeredAddress")}
              onChangeInput={(value) => {
                onSetAddressField("businessLocation", "longAddress", value);
              }}
              inputValue={fields?.businessLocation?.longAddress}
              placeholder={translate("searchAddress")}
              error={fieldHasError("businessLocation")}
              errorMessage={getErrorByField("businessLocation")}
              data-cy={`business-address`}
              disabled={isDisabled}
            />
          </Grid>
        </FieldContainer>
      </Box>

      <Divider className={classes.divider} />

      <Box>
        <Typography className={classes.formHeading}>{translate("details")}</Typography>
        <FieldContainer container spacing={3}>
          <Grid item xs={3}>
            <Input
              name="firstName"
              label={translate("firstName")}
              value={fields.firstName}
              onChange={(event) => onChangeField("firstName", event)}
              placeholder={translate("typeHere")}
              error={fieldHasError("firstName")}
              errorMessage={getErrorByField("firstName")}
              disabled={isDisabled}
            />
          </Grid>
          <Grid item xs={3}>
            <Input
              name="lastName"
              label={translate("lastName")}
              value={fields.lastName}
              onChange={(event) => onChangeField("lastName", event)}
              placeholder={translate("typeHere")}
              error={fieldHasError("lastName")}
              errorMessage={getErrorByField("lastName")}
              disabled={isDisabled}
            />
          </Grid>
          <Grid item xs={3}>
            <PhoneInput
              name="Phone"
              defaultCountry="AU"
              international
              placeholder="61430111900"
              label={translate("phone")}
              error={!!getErrorByField("contactPhone")}
              errorMessage={getErrorByField("contactPhone")}
              value={fields.contactPhone}
              onChange={(value) => onSetField("contactPhone", value)}
              disabled={isDisabled}
            />
          </Grid>
          <Grid item xs={3}>
            <Input
              name="contactEmail"
              label={translate("email")}
              value={fields.contactEmail}
              onChange={(event) => onChangeField("contactEmail", event)}
              placeholder={translate("typeHere")}
              error={fieldHasError("contactEmail")}
              errorMessage={getErrorByField("contactEmail")}
              disabled={isDisabled}
            />
          </Grid>
          <Grid item xs={3}>
            <SkeletonComponent isLoading={isLoadingLanguages} isInput>
              <Select
                label={translate("language")}
                value={fields.contactLanguageId}
                onChange={(event: any) => onChangeField("contactLanguageId", event)}
                disabled={isDisabled}
                placeholder={translate("select")}
              >
                {(languageList || []).map((language, index) => (
                  <MenuItem value={language.id} key={index}>
                    {language.name}
                  </MenuItem>
                ))}
              </Select>
            </SkeletonComponent>
          </Grid>

          <Grid item xs={6}>
            <MapInput
              location={fields?.contactLocation}
              onChange={(location) => {
                onSetField("contactLocation", location);
              }}
              label={translate("personalAddress")}
              onChangeInput={(value) => {
                onSetAddressField("contactLocation", "longAddress", value);
              }}
              inputValue={fields?.contactLocation?.longAddress}
              placeholder={translate("searchAddress")}
              data-cy={`contact-address`}
              disabled={isDisabled}
            />
          </Grid>
        </FieldContainer>

        <Typography className={classes.formHeading}>{translate("bankingDetails")}</Typography>
        <FieldContainer container spacing={3}>
          <Grid item xs={4}>
            <Input
              name="vatCode"
              label={translate("vatCode")}
              value={fields.vatCode}
              onChange={(event) => onChangeField("vatCode", event)}
              placeholder={translate("addNumberHere")}
              error={fieldHasError("vatCode")}
              errorMessage={getErrorByField("vatCode")}
              disabled={isDisabled}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              name="iban"
              label={translate("iban")}
              value={fields.iban}
              onChange={(event) => onChangeField("iban", event)}
              placeholder={translate("addNumberHere")}
              error={fieldHasError("iban")}
              errorMessage={getErrorByField("iban")}
              disabled={isDisabled}
            />
          </Grid>
          <Grid item xs={4}>
            <Input
              name="bic"
              label={translate("bic")}
              value={fields.bic}
              onChange={(event) => onChangeField("bic", event)}
              placeholder={translate("addNumberHere")}
              error={fieldHasError("bic")}
              errorMessage={getErrorByField("bic")}
              disabled={isDisabled}
            />
          </Grid>
          <BillingAddressContainer item xs={8}>
            <MapInput
              location={fields?.billingLocation}
              onChange={(location) => {
                onSetField("billingLocation", location);
              }}
              label={translate("billingAddress")}
              onChangeInput={(value) => {
                onSetAddressField("billingLocation", "longAddress", value);
              }}
              inputValue={fields?.billingLocation?.longAddress}
              placeholder={translate("searchAddress")}
              data-cy={`contact-address`}
              disabled={isDisabled}
            />
          </BillingAddressContainer>
        </FieldContainer>
      </Box>
    </>
  );
};

export default Details;
